import React, { useState, useRef, useEffect } from 'react';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignatureComplete: (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => void;
  title: string;
  clientName?: string;
  previousSignature?: {
    signatureData: string;
    signatureType: 'drawn' | 'typed';
    typedName?: string;
  };
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onClose,
  onSignatureComplete,
  title,
  clientName = '',
  previousSignature
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState<'drawn' | 'typed'>('drawn');
  const [typedName, setTypedName] = useState(clientName);
  const [hasSignature, setHasSignature] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape handler
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap: keep Tab within the dialog
      if (e.key === 'Tab' && dialog) {
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), canvas, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener('keydown', handler);
    // Auto-focus the dialog on open
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])');
      firstFocusable?.focus();
    }
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [isOpen]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (signatureType !== 'drawn') return;

    setIsDrawing(true);
    setHasSignature(true);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || signatureType !== 'drawn') return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasSignature(false);
    setTypedName(clientName);
  };

  const handleSubmit = () => {
    if (signatureType === 'drawn') {
      const canvas = canvasRef.current;
      if (canvas && hasSignature) {
        const signatureData = canvas.toDataURL();
        onSignatureComplete(signatureData, 'drawn');
      }
    } else {
      if (typedName.trim()) {
        onSignatureComplete(typedName.trim(), 'typed', typedName.trim());
      }
    }
  };

  const handleUsePrevious = () => {
    if (previousSignature) {
      onSignatureComplete(
        previousSignature.signatureData,
        previousSignature.signatureType,
        previousSignature.typedName
      );
    }
  };

  const handleTypedNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypedName(e.target.value);
    setHasSignature(e.target.value.trim().length > 0);
  };

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signature-modal-title"
        aria-describedby="signature-modal-description"
        style={{
          backgroundColor: '#28527A',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 id="signature-modal-title" style={{ color: '#d1d5db', fontSize: '1.5rem', margin: '0 0 0.5rem 0' }}>
            {title}
          </h2>
          <p id="signature-modal-description" style={{ color: '#6CA6C1', margin: 0 }}>Please provide your signature for: {title}</p>
        </div>

        {/* Use Previous Signature Option */}
        {previousSignature && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#374151',
            borderRadius: '8px',
            border: '1px solid #6CA6C1'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: '#f9fafb', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
                Use Previous Signature
              </h3>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: 0 }}>
                You can reuse your signature from a previous document
              </p>
            </div>

            {/* Signature Preview */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              padding: '1rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              {previousSignature.signatureType === 'drawn' ? (
                <img
                  src={previousSignature.signatureData}
                  alt="Previous signature"
                  style={{ maxWidth: '200px', maxHeight: '80px' }}
                />
              ) : (
                <div style={{
                  fontSize: '1.2rem',
                  fontFamily: 'cursive',
                  color: '#000',
                  padding: '0.5rem'
                }}>
                  {previousSignature.typedName}
                </div>
              )}
            </div>

            <button
              onClick={handleUsePrevious}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#10b981',
                color: '#f9fafb',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Use This Signature
            </button>
          </div>
        )}

        {/* Divider */}
        {previousSignature && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: '#6b7280'
            }}></div>
            <span style={{
              padding: '0 1rem',
              color: '#d1d5db',
              fontSize: '0.875rem'
            }}>
              OR
            </span>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: '#6b7280'
            }}></div>
          </div>
        )}

        {/* Signature Type Toggle */}
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            backgroundColor: '#374151',
            borderRadius: '8px',
            padding: '4px'
          }}>
            <button
              onClick={() => setSignatureType('drawn')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                minHeight: '44px',
                backgroundColor: signatureType === 'drawn' ? '#3b82f6' : 'transparent',
                color: '#f9fafb',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Draw Signature
            </button>
            <button
              onClick={() => setSignatureType('typed')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                minHeight: '44px',
                backgroundColor: signatureType === 'typed' ? '#3b82f6' : 'transparent',
                color: '#f9fafb',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Type Name
            </button>
          </div>
        </div>

        {/* Signature Area */}
        {signatureType === 'drawn' ? (
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            padding: '1rem'
          }}>
            <canvas
              ref={canvasRef}
              width={500}
              height={200}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              style={{
                width: '100%',
                height: '200px',
                cursor: 'crosshair',
                border: '2px dashed #d1d5db',
                borderRadius: '4px'
              }}
            />
            <p style={{
              textAlign: 'center',
              color: '#d1d5db',
              fontSize: '0.875rem',
              margin: '0.5rem 0 0 0'
            }}>
              Sign above using your mouse or touch device
            </p>
          </div>
        ) : (
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="signature-typed-name" style={{
              display: 'block',
              color: '#f9fafb',
              marginBottom: '0.5rem',
              fontSize: '0.875rem'
            }}>
              Type your full legal name:
            </label>
            <input
              id="signature-typed-name"
              type="text"
              value={typedName}
              onChange={handleTypedNameChange}
              placeholder="Enter your full name"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #374151',
                backgroundColor: '#374151',
                color: '#f9fafb',
                fontSize: '1rem',
                fontFamily: 'cursive',
                textAlign: 'center'
              }}
            />
            {typedName && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#d1d5db',
                  fontSize: '0.875rem',
                  margin: '0 0 0.5rem 0'
                }}>
                  Preview:
                </p>
                <div style={{
                  fontSize: '1.5rem',
                  fontFamily: 'cursive',
                  color: '#000',
                  padding: '0.5rem'
                }}>
                  {typedName}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <button
            onClick={clearSignature}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              minHeight: '44px',
              backgroundColor: '#6b7280',
              color: '#f9fafb',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#374151',
                color: '#f9fafb',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={!hasSignature}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: hasSignature ? '#D4A017' : '#6b7280',
                color: '#f9fafb',
                fontSize: '1rem',
                cursor: hasSignature ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              Sign Document
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;